import { useCallback } from 'react';
import get from 'lodash/get';
import { useApolloClient } from '@apollo/react-hooks';
import type { DocumentNode } from 'graphql';
import { SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants';
import { safeGetValue } from '@atlassian/jira-business-fields/utils';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useFieldsValuesActions } from '@atlassian/jira-issue-field-base';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useQueryParam } from '@atlassian/jira-router';
import { toIssueKey } from '@atlassian/jira-shared-types';
import { OPTIMISTIC_ID_PREFIX } from '../../common/constants';
import type { GenericVariables } from '../../common/types';
import { getParentIssueId } from '../../common/utils/hierarchy';
import type { ListConnection, ListConnectionEdge } from '../pagination/types';
import { getAGGCompliantData } from '../pagination/utils';
import type {
	AddToCacheData,
	BaseIssueNode,
	BaseListData,
	JWMGetItemsCacheVariables,
	RemoveIssueFromExtractedFieldsCache,
	UpdateItemsInCacheData,
} from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getIssueKey = (fields: ReadonlyArray<any>) =>
	safeGetValue<string>(fields, 'issuekey', 'text', '');

const getIndexById = (listData: ListConnection<BaseIssueNode> | undefined, issueId: number) => {
	if (listData == null || listData.edges == null) {
		return -1;
	}

	const { edges } = listData;
	return edges.findIndex(
		(edge) =>
			edge != null &&
			edge.node != null &&
			edge.node.issueId != null &&
			edge.node.issueId === issueId,
	);
};
/**
 * This service responsible for modifying the apollo cache
 * and also syncing the cache with extracted fields cache
 *
 * RELATIONSHIP WITH EXTRACTED FIELDS:
 * Extracted fields have their own cache and they don't go through network if the cache is populated.
 * Removing a row and not emptying the extracted fields cache will mean that when the issue is added back some fields will be stale,
 * despite having being fetched with the new value, because extracted fields will ignore the passed value and use the value from the cache.
 * If we empty the cache, next time we add the item, extracted field will bootstrap it as if it was the first time it appeared, avoiding stale data.
 *
 * EXPLANATION OF GENERIC AND HOW THE `addToCache` WORKS:
 * The generic parameter in this hook is specific to the implementation of list GraphQL query where
 * - we have 1 query type for the whole list (TListData)
 * - inside the TListData properties, there is `TIssueNode`, this is the type for every issue
 * TListData and TIssueNode must be connected to each other as defined in GQL types
 *
 * This function will then get the listData from apollo cache, and append the passed issueData (`TIssueNode`) into the list data
 */
export const useCacheActions = () => {
	const analyticsTaskName = 'useCacheActionsApolloCacheActions';
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const client = useApolloClient();
	const [, extractedFieldValueActions] = useFieldsValuesActions();
	const [currentIssueKey] = useQueryParam(SELECTED_ISSUE_PARAM);

	const removeIssueFromExtractedFieldsCache = useCallback(
		({ currentIssueId, issueKey, parentIssueId }: RemoveIssueFromExtractedFieldsCache) => {
			issueKey != null &&
				// do not clean the cache if the issue is currently visible in bento (that would show blank fields)
				issueKey !== currentIssueKey &&
				`${parentIssueId}` !== `${currentIssueId}` &&
				extractedFieldValueActions.setIssue(toIssueKey(issueKey), {});
		},
		[currentIssueKey, extractedFieldValueActions],
	);

	const addToCache = useCallback(
		<TIssueNode extends BaseIssueNode, TListData extends BaseListData<TIssueNode>>({
			query,
			variables,
			newNodes,
		}: AddToCacheData<TIssueNode>) => {
			const getCachedListData = () => {
				try {
					const read = client.readQuery<TListData, JWMGetItemsCacheVariables>({
						query,
						variables,
					});

					fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheRead taskSuccess', {
						task: analyticsTaskName,
					});

					return read;
				} catch (e) {
					fireErrorAnalytics({
						error: e instanceof Error ? e : new Error('Unknown cache read error'),
						meta: {
							id: 'addToCache',
							packageName: 'jiraBusinessListHandlers',
							teamName: 'keystone',
						},
						sendToPrivacyUnsafeSplunk: true,
						skipSentry: true,
						attributes: {
							task: analyticsTaskName,
							query: JSON.stringify(query),
							variables: JSON.stringify(variables),
							eventName: 'apolloCacheRead taskFail',
							error: JSON.stringify(e),
						},
					});
					return null;
				}
			};

			const data = getCachedListData();

			const listData = getAGGCompliantData<TListData, TIssueNode>(data ?? undefined, 'list');

			if (
				listData == null ||
				listData.edges == null ||
				!Array.isArray(newNodes) ||
				newNodes.length === 0
			) {
				return;
			}
			try {
				const newIssueIds = new Set(
					newNodes
						.map((item) => {
							if (item != null && item.issueId != null) {
								return item.issueId;
							}
							return undefined;
						})
						.filter(Boolean),
				);

				newNodes.forEach((item) => {
					if (item == null || item.fields == null) {
						return;
					}
					const issueKey = getIssueKey(item.fields);
					if (issueKey != null && issueKey !== '') {
						const parentIssueId = getParentIssueId(item);
						const currentIssueId: number = item.issueId;

						removeIssueFromExtractedFieldsCache({
							currentIssueId,
							issueKey,
							parentIssueId,
						});
					}
				});

				const isOptimisticIssueWithRealIssueInNewNodes = (issue: BaseIssueNode) => {
					// check whether an issue is an optimisticIssue that has the real data coming in
					// summary should give us a second check to replace the right optimistic issue with the new one
					// we are using summary instead of issueKey or issueId
					// since it's the only field that will contain the same value to identify
					// the correlation between real issue and optimistic issue

					const issueSummary = issue.fields?.find((field) => field.fieldId === 'summary');
					const newNodeWithSameSummary = newNodes.find((newNode) =>
						newNode.fields?.find(
							(field) =>
								field.fieldId === 'summary' &&
								issueSummary != null &&
								field.text === issueSummary.text,
						),
					);

					return (
						issue.id.toString().startsWith(OPTIMISTIC_ID_PREFIX) && newNodeWithSameSummary != null
					);
				};

				const newIssues =
					listData.edges
						.filter(
							(edge) =>
								edge != null &&
								edge.node != null &&
								edge.node.issueId != null &&
								!newIssueIds.has(edge.node.issueId) &&
								!isOptimisticIssueWithRealIssueInNewNodes(edge.node),
						)
						.concat(
							newNodes.map((node) => ({
								node,
								__typename: 'JiraWorkManagementViewItemEdge',
							})),
						) ?? [];

				client.writeQuery({
					query,
					variables,
					data: {
						...data,
						jira: {
							...(data?.jira ?? {}),
							list: {
								...listData,
								edges: newIssues,
								totalCount: newIssues.length,
							},
						},
					},
				});

				fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheWrite taskSuccess', {
					task: analyticsTaskName,
				});

				fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloAddToCache taskSuccess', {
					task: analyticsTaskName,
				});
			} catch (e) {
				fireErrorAnalytics({
					error: e instanceof Error ? e : new Error('Unknown cache write error'),
					meta: {
						id: 'addToCache',
						packageName: 'jiraBusinessListHandlers',
						teamName: 'keystone',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
					attributes: {
						task: analyticsTaskName,
						query: JSON.stringify(query),
						variables: JSON.stringify(variables),
						eventName: 'apolloCacheWrite taskFail',
						error: JSON.stringify(e),
					},
				});
			}
		},
		[client, createAnalyticsEvent, removeIssueFromExtractedFieldsCache],
	);

	const removeFromCache = useCallback(
		<TIssueNode extends BaseIssueNode, TListData extends BaseListData<TIssueNode>>({
			query,
			variables,
			issueId,
		}: {
			query: DocumentNode;
			variables: GenericVariables;
			issueId: number | number[];
		}) => {
			const issueIds = Array.isArray(issueId) ? issueId : [issueId];

			const getCachedListData = () => {
				try {
					const read = client.readQuery<TListData, GenericVariables>({
						query,
						variables,
					});

					fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheRead taskSuccess', {
						task: analyticsTaskName,
					});

					return read;
				} catch (e) {
					fireErrorAnalytics({
						error: e instanceof Error ? e : new Error('Unknown cache read error'),
						meta: {
							id: 'removeFromCache',
							packageName: 'jiraBusinessListHandlers',
							teamName: 'keystone',
						},
						sendToPrivacyUnsafeSplunk: true,
						skipSentry: true,
						attributes: {
							task: analyticsTaskName,
							query: JSON.stringify(query),
							variables: JSON.stringify(variables),
							eventName: 'apolloCacheRead taskFail',
							error: JSON.stringify(e),
						},
					});
					return null;
				}
			};

			const data = getCachedListData();

			const listData = getAGGCompliantData<TListData, TIssueNode>(data ?? undefined, 'list');

			if (!listData?.edges || !Array.isArray(listData.edges)) {
				return;
			}

			try {
				const updatedEdges = listData.edges?.filter(
					(edge) => edge?.node && !issueIds.includes(edge.node.issueId),
				);

				client.writeQuery({
					query,
					variables,
					data: {
						...data,
						jira: {
							...(data?.jira ?? {}),
							list: {
								...listData,
								edges: updatedEdges,
								totalCount: updatedEdges.length,
							},
						},
					},
				});

				fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheWrite taskSuccess', {
					task: analyticsTaskName,
				});

				issueIds.forEach((issue) => {
					const index = getIndexById(listData ?? undefined, issue);

					const item = updatedEdges[index];
					const issueKey = getIssueKey(item?.node?.fields ?? []);
					const currentIssueId: number = get(item, 'issueId', 0);

					if (issueKey !== '') {
						removeIssueFromExtractedFieldsCache({ currentIssueId, issueKey });
					}
				});

				fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloRemoveFromCache taskSuccess', {
					task: analyticsTaskName,
				});
			} catch (e) {
				fireErrorAnalytics({
					error: e instanceof Error ? e : new Error('Unknown cache Write error'),
					meta: {
						id: 'removeFromCache',
						packageName: 'jiraBusinessListHandlers',
						teamName: 'keystone',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
					attributes: {
						task: analyticsTaskName,
						query: JSON.stringify(query),
						variables: JSON.stringify(variables),
						eventName: 'apolloCacheWrite taskFail',
						error: JSON.stringify(e),
					},
				});
			}
		},
		[client, createAnalyticsEvent, removeIssueFromExtractedFieldsCache],
	);

	const getCachedIssueById = useCallback(
		<TIssueNode extends BaseIssueNode, TListData extends BaseListData<TIssueNode>>({
			query,
			variables,
			issueId,
		}: {
			issueId: string;
			query: DocumentNode;
			variables: GenericVariables;
		}): TIssueNode | null => {
			const getCachedListData = () => {
				try {
					const read = client.readQuery<TListData, GenericVariables>({
						query,
						variables,
					});

					fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheRead taskSuccess', {
						task: analyticsTaskName,
					});

					return read;
				} catch (e) {
					fireErrorAnalytics({
						error: e instanceof Error ? e : new Error('Unknown cache read error'),
						meta: {
							id: 'getCachedIssueById',
							packageName: 'jiraBusinessListHandlers',
							teamName: 'keystone',
						},
						sendToPrivacyUnsafeSplunk: true,
						skipSentry: true,
						attributes: {
							task: analyticsTaskName,
							query: JSON.stringify(query),
							variables: JSON.stringify(variables),
							eventName: 'apolloCacheRead taskFail',
							error: JSON.stringify(e),
						},
					});
					return null;
				}
			};

			const data = getCachedListData();

			const listData = getAGGCompliantData<TListData, TIssueNode>(data ?? undefined, 'list');

			fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloGetCachedIssueById taskSuccess', {
				task: analyticsTaskName,
			});
			return (
				listData?.edges?.find((issue) => issue?.node?.issueId === Number(issueId))?.node ?? null
			);
		},
		[client, createAnalyticsEvent],
	);

	const updateItemsInCache = useCallback(
		<TIssueNode extends BaseIssueNode, TListData extends BaseListData<TIssueNode>>({
			query,
			variables,
			issueIds,
			updateItem,
		}: UpdateItemsInCacheData<TIssueNode>): void => {
			const getCachedListData = () => {
				try {
					const read = client.readQuery<TListData, JWMGetItemsCacheVariables>({
						query,
						variables,
					});

					fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheRead taskSuccess', {
						task: analyticsTaskName,
					});

					return read;
				} catch (e) {
					fireErrorAnalytics({
						error: e instanceof Error ? e : new Error('Unknown cache read error'),
						meta: {
							id: 'updateItemsInCache',
							packageName: 'jiraBusinessListHandlers',
							teamName: 'keystone',
						},
						sendToPrivacyUnsafeSplunk: true,
						skipSentry: true,
						attributes: {
							task: analyticsTaskName,
							query: JSON.stringify(query),
							variables: JSON.stringify(variables),
							eventName: 'apolloCacheRead taskFail',
							error: JSON.stringify(e),
						},
					});
					return null;
				}
			};

			const data = getCachedListData();

			const listData = getAGGCompliantData<TListData, TIssueNode>(data ?? undefined, 'list');

			const existingEdges = listData?.edges;
			if (existingEdges == null) {
				return;
			}

			try {
				const newEdges: (ListConnectionEdge<TIssueNode> | null)[] = [...existingEdges];
				let hasItemOverride = false;

				issueIds.forEach((issueId) => {
					const issueToUpdateIndex =
						existingEdges.findIndex((issue) => issue?.node?.issueId === Number(issueId)) ??
						undefined;
					const existingIssue = existingEdges[issueToUpdateIndex];
					if (
						existingIssue?.node == null ||
						issueToUpdateIndex === undefined ||
						issueToUpdateIndex === -1 ||
						existingIssue == null
					) {
						return;
					}

					const itemOverride = updateItem(existingIssue.node);
					if (itemOverride === undefined) {
						return;
					}
					hasItemOverride = true;

					newEdges[issueToUpdateIndex] = {
						node: itemOverride,
						__typename: 'JiraWorkManagementViewItemEdge',
					};
				});

				if (!hasItemOverride) return;

				client.writeQuery({
					query,
					variables,
					data: {
						...data,
						jira: {
							...(data?.jira ?? {}),
							list: {
								...listData,
								edges: newEdges,
								totalCount: newEdges.length,
							},
						},
					},
				});
				fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheWrite taskSuccess', {
					task: analyticsTaskName,
				});

				fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloUpdateItemsInCache taskSuccess', {
					task: analyticsTaskName,
				});
			} catch (e) {
				fireErrorAnalytics({
					error: e instanceof Error ? e : new Error('Unknown cache write error'),
					meta: {
						id: 'updateItemsInCache',
						packageName: 'jiraBusinessListHandlers',
						teamName: 'keystone',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
					attributes: {
						task: analyticsTaskName,
						query: JSON.stringify(query),
						variables: JSON.stringify(variables),
						eventName: 'apolloCacheWrite taskFail',
						error: JSON.stringify(e),
					},
				});
			}
		},
		[client, createAnalyticsEvent],
	);

	return {
		addToCache,
		removeFromCache,
		getCachedIssueById,
		updateItemsInCache,
	};
};

export type UseCacheActionsReturn = ReturnType<typeof useCacheActions>;
export type AddToCache = UseCacheActionsReturn['addToCache'];
export type RemoveFromCache = UseCacheActionsReturn['removeFromCache'];
export type UpdateItemsInCache = UseCacheActionsReturn['updateItemsInCache'];
